import { MouseEvent, useRef, useState } from 'react'

import CloseIcon from '@mui/icons-material/Close'
import OutlineInfoIcon from '@mui/icons-material/InfoOutline'
import { Button, IconButton, InputAdornment } from '@mui/material'
import { ConsoleTooltip } from '@/components/ConsoleTooltip'

import { ConsoleTextField } from '../StyledInputComponents'

export type FileUploadInputProps = {
  label?: string
  defaultValue?: File | null
  onChange?: (file: File | null) => void
  accept?: string
  onClear?: () => void
  testId?: string
  disabled?: boolean
  disabledInfo?: string
}

export const FileUploadInput = ({
  defaultValue = null,
  onChange,
  accept,
  onClear,
  testId,
  disabled,
  disabledInfo,
  label = 'Upload File'
}: FileUploadInputProps) => {
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [selectedFile, setSelectedFile] = useState<File | null>(defaultValue)

  const handleUploadClick = () => {
    if (!disabled && fileInputRef.current) {
      fileInputRef.current.click()
    }
  }

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0] || null
    setSelectedFile(file)
    onChange?.(file)

    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
  }

  const handleClear = (e: MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation()
    setSelectedFile(null)
    onChange?.(null)
    onClear?.()
  }

  const displayValue = selectedFile?.name || 'No file selected'

  return (
    <>
      <ConsoleTextField
        label={label}
        value={displayValue}
        disabled={disabled}
        testId={testId}
        onClick={handleUploadClick}
        slotProps={{
          input: {
            readOnly: true,
            endAdornment: (
              <InputAdornment position="end" sx={{ mr: -1.6 }}>
                {selectedFile && !disabled && (
                  <IconButton
                    onClick={handleClear}
                    size="small"
                    data-testid={testId ? `${testId}-clear` : undefined}
                  >
                    <CloseIcon />
                  </IconButton>
                )}

                <Button
                  variant="text"
                  disabled={disabled}
                  data-testid={testId ? `${testId}-upload` : undefined}
                >
                  Upload
                </Button>

                {disabled && disabledInfo && (
                  <ConsoleTooltip title={disabledInfo}>
                    <OutlineInfoIcon sx={{ mr: 1.5 }} />
                  </ConsoleTooltip>
                )}
              </InputAdornment>
            )
          }
        }}
        sx={{
          '& .MuiInputBase-input': {
            cursor: 'default',
            color: selectedFile ? 'text.primary' : 'text.secondary'
          }
        }}
      />
      <input
        ref={fileInputRef}
        type="file"
        accept={accept}
        onChange={handleFileChange}
        style={{ display: 'none' }}
        disabled={disabled}
        data-testid={testId ? `${testId}-hidden-input` : undefined}
      />
    </>
  )
}
